import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Typography } from "@aviary";
import { isValidPhoneNumber } from "@aviary/components/PhoneInput/validatePhoneNumber";
import { allFormFieldsFilled } from "@helpers/validation";
import { ValidationSummary } from "@shared/components/ValidationSummary/ValidationSummary";
import { useMerchantDataAddressUpdate } from "@shared/data/mutations/MerchantDataAddressUpdateMutation/MerchantDataAddressUpdate.mutation";
import { usePractitionerCertificationCreateMutation } from "@shared/data/mutations/PractitionerCertificationCreateMutation/PractitionerCertificationCreate.mutation";
import { useStudentProfileCreateMutation } from "@shared/data/mutations/StudentProfileCreateMutation/StudentProfileCreate.mutation";
import type {
  StudentInformationErrors,
  StudentInformationData,
} from "@shared/data/mutations/StudentProfileCreateMutation/StudentProfileCreate.mutation";
import { useCurrentPractitionerIsStudentQuery } from "@shared/data/queries/CurrentPractitionerIsStudentQuery/CurrentPractitionerIsStudent.query";
import type { CredentialsErrors } from "@shared/types/credentials";
import type { AddressInformationErrors, PartialAddressInformationData } from "@shared/types/fields";
import { l } from "@unauthenticated/oauth/locales/i18n";

import { OAuthStepHeader } from "../OAuthStepHeader";
import { OAuthStep } from "../oauthStep";

import { OAuthStudentInfoFields } from "./OAuthStudentInfoFields/OAuthStudentInfoFields";
import { PartialAddressFields } from "./PartialAddressFields";
import { UploadCredentials } from "./UploadCredentials";

import * as styles from "./OAuthCertificationStep.styles";

interface Props {
  onUpdateCurrentStep: (step: OAuthStep) => void;
  onUpdateStoreCurrentSetupPage: (step: string) => Promise<void>;
  onOnboardingComplete: () => void;
}

const OAuthCertificationStep = ({
  onOnboardingComplete,
  onUpdateCurrentStep,
  onUpdateStoreCurrentSetupPage,
}: Props) => {
  const { t } = useTranslation();
  const [credential, setCredential] = useState<File[]>([]);
  const [credentialsErrors, setCredentialsErrors] = useState<CredentialsErrors>(null);
  const [addressInformationData, setAddressInformationData] =
    useState<PartialAddressInformationData>({
      region: "",
      postalCode: "",
      phone: "",
    });

  const [addressInformationErrors, setAddressInformationErrors] =
    useState<AddressInformationErrors>(null);

  const [studentInformationData, setStudentInformationData] = useState<StudentInformationData>({
    practitionerTypeId: "",
    graduationDate: "",
  });

  const [studentInformationErrors, setStudentInformationErrors] =
    useState<StudentInformationErrors>(null);

  const { data: isStudentData, loading: isStudentLoading } = useCurrentPractitionerIsStudentQuery();

  const isStudent = !isStudentLoading && isStudentData?.currentPractitioner?.properties?.isStudent;

  const studentsFieldsFilled = isStudent ? allFormFieldsFilled(studentInformationData) : true;
  const getTopLevelErrors = () => [
    ...(studentInformationErrors?.message ? [studentInformationErrors.message] : []),
    ...(addressInformationErrors?.message ? [addressInformationErrors.message] : []),
    ...(credentialsErrors?.message ? [credentialsErrors.message] : []),
  ];

  const areFieldsFilled =
    studentsFieldsFilled &&
    allFormFieldsFilled(addressInformationData) &&
    isValidPhoneNumber(addressInformationData.phone);

  const [practitionerCertificationCreate, { loading: practitionerCertUploadLoading }] =
    usePractitionerCertificationCreateMutation({
      onError: errors => setCredentialsErrors({ message: errors.message }),
    });

  const [studentProfileCreate, { loading: studentProfileCreateLoading }] =
    useStudentProfileCreateMutation({
      onError: errors => setStudentInformationErrors({ message: errors.message }),
    });

  const resetFieldErrors = () => {
    setStudentInformationErrors(null);
    setAddressInformationErrors(null);
  };

  const [updateMerchantDataAddress, { loading: merchantDataAddressUpdateLoading }] =
    useMerchantDataAddressUpdate({
      onError: errors => setAddressInformationErrors({ message: errors.message }),
    });

  const credentialTerm = isStudent ? t(l.oauth.proofOfEnrollment) : t(l.oauth.certification);
  const handleSubmit = async () => {
    resetFieldErrors();
    const hasCredentials = !!credential?.length;

    if (isStudent) {
      const { data: studentProfileCreateData } = await studentProfileCreate({
        variables: {
          input: {
            attributes: studentInformationData,
          },
        },
      });

      const studentErrors = studentProfileCreateData?.studentProfileCreate.errors;

      if (studentErrors?.fields) {
        setStudentInformationErrors(studentErrors);

        return;
      } else {
        await onUpdateStoreCurrentSetupPage("certificate");
      }
    }

    const updatedAddressInformation = {
      ...addressInformationData,
      postalCode: addressInformationData.postalCode.toUpperCase(),
    };

    const { data: addressMutationData } = await updateMerchantDataAddress({
      variables: {
        input: {
          attributes: {
            ...updatedAddressInformation,
          },
        },
      },
    });

    const addressErrors = addressMutationData?.merchantDataAddressUpdate.errors;

    if (addressErrors?.fields || addressErrors?.message) {
      setAddressInformationErrors(addressErrors);

      return;
    }

    if (hasCredentials) {
      const { data: certificationMutationData } = await practitionerCertificationCreate({
        variables: {
          input: {
            attributes: {
              certificate: credential[0],
            },
          },
        },
      });

      const certificationErrors =
        certificationMutationData?.practitionerCertificationCreate?.errors;

      if (certificationErrors?.fields || certificationErrors?.message) {
        setCredentialsErrors(certificationErrors);

        return;
      }

      if (isStudent) {
        await onUpdateStoreCurrentSetupPage("complete");
        onOnboardingComplete();
      } else {
        onUpdateStoreCurrentSetupPage("account_type");
        onUpdateCurrentStep(OAuthStep.ACCOUNT_TYPE);
      }
    } else {
      if (isStudent) {
        onUpdateStoreCurrentSetupPage("student_identification");
      } else {
        onUpdateStoreCurrentSetupPage("certificate");
      }
      onOnboardingComplete();
    }
  };

  const renderActions = () => (
    <div css={styles.actions}>
      <Button
        css={styles.button}
        isOutlined
        disabled={!!credential?.length || !areFieldsFilled}
        isLoading={
          !credential?.length && (studentProfileCreateLoading || merchantDataAddressUpdateLoading)
        }
        onClick={handleSubmit}
      >
        {t(l.oauth.FinishTheRestLater)}
      </Button>
      <Button
        css={styles.button}
        isLoading={
          !!credential?.length &&
          (practitionerCertUploadLoading ||
            merchantDataAddressUpdateLoading ||
            studentProfileCreateLoading)
        }
        disabled={!(areFieldsFilled && !!credential?.length)}
        onClick={handleSubmit}
      >
        {t(l.oauth.Next)}
      </Button>
    </div>
  );

  if (isStudentLoading) return null; // todo: skeleton

  return (
    <div css={styles.main}>
      <OAuthStepHeader hideStepper={isStudent} currentStep={OAuthStep.CERTIFICATION} />
      <div>
        <div css={styles.descriptionContainer}>
          <Typography css={styles.description}>
            {t(l.oauth.AddYourCredential, { credentialTerm })}
          </Typography>
        </div>
        <ValidationSummary errors={getTopLevelErrors()} />
        {isStudent && (
          <OAuthStudentInfoFields
            studentInformationData={studentInformationData}
            handleSetStudentInformationData={studentInfo => setStudentInformationData(studentInfo)}
            studentInformationErrors={studentInformationErrors}
          />
        )}
        <PartialAddressFields
          addressInformationData={addressInformationData}
          handleSetAddressInformation={addressInfo => setAddressInformationData(addressInfo)}
          addressInformationErrors={addressInformationErrors}
        />

        <UploadCredentials
          isStudent={isStudent}
          isDisabled={!areFieldsFilled}
          handleSetCredentials={files => setCredential(files)}
          credentialsErrors={credentialsErrors}
        />
        {renderActions()}
      </div>
    </div>
  );
};

export { OAuthCertificationStep };
